import { useState, useEffect, useCallback, useRef } from 'react';
import type { AutocompleteProvider } from '../../types';
import type {
  AutocompleteAdapter,
  AutocompleteOptions,
  ProcessedAutocompleteItem,
  InsertRange,
} from '../core/types';
import {
  buildContext,
  processProviderResponse,
  getOrCreateSelectableItem,
} from '../core/queryProcessor';

export interface UseAutocompleteOptions extends AutocompleteOptions {
  /** The adapter for the specific editor type */
  adapter: AutocompleteAdapter | null;
  /** The autocomplete provider */
  provider: AutocompleteProvider;
  /** Callback when autocomplete closes */
  onClose?: () => void;
}

export interface UseAutocompleteReturn {
  /** Whether the autocomplete popup is open */
  isOpen: boolean;
  /** Items to display in the popup */
  items: ProcessedAutocompleteItem[];
  /** Current query string */
  query: string;
  /** Position for the popup */
  position: { x: number; y: number } | null;
  /** Currently selected item index */
  selectedIndex: number;
  /** Handle item selection */
  selectItem: (index: number) => void;
  /** Handle keyboard navigation */
  onKeyDown: (event: KeyboardEvent) => boolean;
  /** Close the autocomplete popup */
  close: () => void;
  /** Navigate selection up */
  navigateUp: () => void;
  /** Navigate selection down */
  navigateDown: () => void;
}

export function useAutocomplete(
  options: UseAutocompleteOptions
): UseAutocompleteReturn {
  const { adapter, provider, showDescriptions, allowFreeformHandlebars, onClose } = options;

  const [isOpen, setIsOpen] = useState(false);
  const [items, setItems] = useState<ProcessedAutocompleteItem[]>([]);
  const [query, setQuery] = useState('');
  const [position, setPosition] = useState<{ x: number; y: number } | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [range, setRange] = useState<InsertRange | null>(null);

  // Track if we're currently querying to avoid race conditions
  const queryingRef = useRef(false);
  const lastQueryRef = useRef('');

  // Keep refs in sync with state for synchronous access in event handlers
  // This avoids race conditions when typing quickly (e.g., }})
  const queryRef = useRef(query);
  const itemsRef = useRef(items);
  const rangeRef = useRef(range);
  const isOpenRef = useRef(isOpen);

  // Update refs when state changes
  queryRef.current = query;
  itemsRef.current = items;
  rangeRef.current = range;
  isOpenRef.current = isOpen;

  // Check for trigger and query provider
  const checkTrigger = useCallback(async () => {
    if (!adapter) return;

    const trigger = adapter.detectTrigger();

    if (!trigger) {
      if (isOpen) {
        setIsOpen(false);
        isOpenRef.current = false;
        onClose?.();
      }
      return;
    }

    // Get popup position
    const rect = adapter.getCursorRect();
    if (rect) {
      setPosition({ x: rect.left, y: rect.bottom });
    }

    // Skip if same query
    if (trigger.query === lastQueryRef.current && isOpen) {
      return;
    }

    lastQueryRef.current = trigger.query;
    // Update both state and refs (refs update synchronously for event handlers)
    setQuery(trigger.query);
    queryRef.current = trigger.query;
    setRange(trigger.range);
    rangeRef.current = trigger.range;

    // Query provider
    queryingRef.current = true;
    const blockContext = adapter.getBlockContext();
    const context = buildContext(trigger.query, blockContext, adapter.editor);

    const processedItems = await processProviderResponse(
      provider,
      trigger.query,
      context,
      { showDescriptions, allowFreeformHandlebars }
    );

    queryingRef.current = false;

    // Note: isComplete items are NOT auto-inserted. They are shown in the popup
    // and the user must explicitly select (Enter/Tab) or type }} to insert.
    // This matches the behavior of the original HandlebarsSuggestion.

    // Update both state and refs (refs update synchronously for event handlers)
    setItems(processedItems);
    itemsRef.current = processedItems;
    setSelectedIndex(0);
    setIsOpen(true);
    isOpenRef.current = true;
  }, [adapter, provider, showDescriptions, allowFreeformHandlebars, isOpen, onClose]);

  // Subscribe to editor updates
  useEffect(() => {
    if (!adapter) return;

    const unsubUpdate = adapter.onUpdate(checkTrigger);
    const unsubSelection = adapter.onSelectionUpdate(checkTrigger);

    // Initial check
    checkTrigger();

    return () => {
      unsubUpdate();
      unsubSelection();
    };
  }, [adapter, checkTrigger]);

  // Handle item selection
  const selectItem = useCallback(
    (index: number) => {
      if (!adapter || !range) return;

      const item = items[index];
      if (!item || item.isError) return;

      adapter.insertItem(item, range, { showDescriptions, allowFreeformHandlebars });
      setIsOpen(false);
      lastQueryRef.current = '';
      onClose?.();
    },
    [adapter, items, range, showDescriptions, allowFreeformHandlebars, onClose]
  );

  // Navigation
  const navigateUp = useCallback(() => {
    setSelectedIndex((prev) => (prev <= 0 ? items.length - 1 : prev - 1));
  }, [items.length]);

  const navigateDown = useCallback(() => {
    setSelectedIndex((prev) => (prev >= items.length - 1 ? 0 : prev + 1));
  }, [items.length]);

  // Close handler
  const close = useCallback(() => {
    setIsOpen(false);
    isOpenRef.current = false;
    lastQueryRef.current = '';
    onClose?.();
  }, [onClose]);

  // Keyboard handler
  const onKeyDown = useCallback(
    (event: KeyboardEvent): boolean => {
      // Handle }} completion first - this should work even if popup state appears "closed"
      // due to React state update lag. Use refs for synchronous access.
      if (event.key === '}') {
        const currentQuery = queryRef.current;
        const currentItems = itemsRef.current;
        const currentRange = rangeRef.current;

        if (currentQuery.endsWith('}') && currentRange && adapter) {
          event.preventDefault();
          const selectableItem = getOrCreateSelectableItem(
            currentItems,
            currentQuery,
            { allowFreeformHandlebars }
          );
          if (selectableItem) {
            adapter.insertItem(selectableItem, currentRange, { showDescriptions, allowFreeformHandlebars });
            setIsOpen(false);
            lastQueryRef.current = '';
            onClose?.();
            return true;
          }
        }
        return false;
      }

      // All other key handling requires popup to be open
      if (!isOpen) return false;

      switch (event.key) {
        case 'Escape':
          close();
          return true;

        case 'ArrowUp':
          event.preventDefault();
          navigateUp();
          return true;

        case 'ArrowDown':
          event.preventDefault();
          navigateDown();
          return true;

        case 'Enter':
        case 'Tab':
          event.preventDefault();
          selectItem(selectedIndex);
          return true;

        default:
          return false;
      }
    },
    [
      isOpen,
      close,
      navigateUp,
      navigateDown,
      selectItem,
      selectedIndex,
      adapter,
      showDescriptions,
      allowFreeformHandlebars,
      onClose,
    ]
  );

  return {
    isOpen,
    items,
    query,
    position,
    selectedIndex,
    selectItem,
    onKeyDown,
    close,
    navigateUp,
    navigateDown,
  };
}
